
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { buildContentfulImageURL } from '@utils/image';
import Image from '../../../../components/Image';
import ImageWrapper from '../../../../components/ImageWrapper';
import AnchorLink from '../../../../components/AnchorLink';
import s from './ProfilePromo.module.scss';
import UserSpecificPromo from '../../../../components/UserSpecificPromo';
import { analyticsTags } from '../../../../utils';
import { trackEvent } from '../../../../utils/analytics';
import { findHubName } from '../../../../utils/hubs';
const propTypes = {
    promo: PropTypes.shape({
        url: PropTypes.string,
        id: PropTypes.string,
        contentfulId: PropTypes.string,
        activationDate: PropTypes.string,
        externalId: PropTypes.string,
        mainImage: PropTypes.shape({
            image: PropTypes.shape({
                file: PropTypes.shape({
                    url: PropTypes.string
                })
            })
        }),
        teaserImage: PropTypes.shape({
            image: PropTypes.shape({
                file: PropTypes.shape({
                    url: PropTypes.string
                })
            })
        }),
        promoHeader: PropTypes.string,
        contentTypeId: PropTypes.string,
        tags: PropTypes.array,
        displayTitle: PropTypes.string,
        entryTitle: PropTypes.string
    }).isRequired,
    section: PropTypes.shape({
        sectionTitle: PropTypes.string,
        promos: PropTypes.array
    })
};
const isPromoContent = (promo) => {
    return ['article', 'collection', 'writingByJKRowling'].includes(promo.contentTypeId);
};
const pageName = 'Profile';
const ProfilePromo = ({ promo, section = {
    sectionTitle: null,
    promos: []
}, }) => {
    const { externalId, displayTitle, entryTitle, contentTypeId, contentfulId, tags = [], activationDate, } = promo;
    const { sectionTitle, promos } = section;
    const [promoRef, promoInView] = useInView({
        threshold: 1,
        triggerOnce: true
    });
    const aspectRatio = {
        mobile: [450, 253],
        tablet: [700, 395],
        desktop: [1320, 743]
    };
    const [mobileWidth, mobileHeight] = aspectRatio.mobile;
    const [tabletWidth, tabletHeight] = aspectRatio.tablet;
    const [desktopWidth, desktopHeight] = aspectRatio.desktop;
    const alignment = 'top';
    const imageSet = (contentfulImage) => {
        return [
            {
                src: `${buildContentfulImageURL(contentfulImage, {
                    width: mobileWidth,
                    height: mobileHeight,
                    fit: 'fill',
                    focus: alignment,
                    format: 'webp'
                })} 1x, ${buildContentfulImageURL(contentfulImage, {
                    width: mobileWidth * 2,
                    height: mobileHeight * 2,
                    fit: 'fill',
                    focus: alignment,
                    format: 'webp'
                })} 2x`,
                media: '(max-width: 767px)',
                type: 'image/webp'
            },
            {
                src: `${buildContentfulImageURL(contentfulImage, {
                    width: mobileWidth,
                    height: mobileHeight,
                    fit: 'fill',
                    focus: alignment,
                    format: 'jpg'
                })} 1x, ${buildContentfulImageURL(contentfulImage, {
                    width: mobileWidth * 2,
                    height: mobileHeight * 2,
                    fit: 'fill',
                    focus: alignment,
                    format: 'jpg'
                })} 2x`,
                media: '(max-width: 767px)',
                type: 'image/jpeg'
            },
            {
                src: `${buildContentfulImageURL(contentfulImage, {
                    width: tabletWidth,
                    height: tabletHeight,
                    fit: 'fill',
                    focus: alignment,
                    format: 'webp'
                })} 1x, ${buildContentfulImageURL(contentfulImage, {
                    width: tabletWidth * 2,
                    height: tabletHeight * 2,
                    fit: 'fill',
                    focus: alignment,
                    format: 'webp'
                })} 2x`,
                media: '(max-width: 1041px)',
                type: 'image/webp'
            },
            {
                src: `${buildContentfulImageURL(contentfulImage, {
                    width: tabletWidth,
                    height: tabletHeight,
                    fit: 'fill',
                    focus: alignment,
                    format: 'jpg'
                })} 1x, ${buildContentfulImageURL(contentfulImage, {
                    width: tabletWidth * 2,
                    height: tabletHeight * 2,
                    fit: 'fill',
                    focus: alignment,
                    format: 'jpg'
                })} 2x`,
                media: '(max-width: 1041px)',
                type: 'image/jpeg'
            },
            {
                src: `${buildContentfulImageURL(contentfulImage, {
                    width: desktopWidth,
                    height: desktopHeight,
                    fit: 'fill',
                    focus: alignment,
                    format: 'webp'
                })} 1x, ${buildContentfulImageURL(contentfulImage, {
                    width: desktopWidth * 2,
                    height: desktopHeight * 2,
                    fit: 'fill',
                    focus: alignment,
                    format: 'webp'
                })} 2x`,
                type: 'image/webp'
            },
            {
                src: `${buildContentfulImageURL(contentfulImage, {
                    width: desktopWidth,
                    height: desktopHeight,
                    fit: 'fill',
                    focus: alignment,
                    format: 'jpg'
                })} 1x, ${buildContentfulImageURL(contentfulImage, {
                    width: desktopWidth * 2,
                    height: desktopHeight * 2,
                    fit: 'fill',
                    focus: alignment,
                    format: 'jpg'
                })} 2x`,
                type: 'image/jpeg'
            },
            {
                src: buildContentfulImageURL(contentfulImage, {
                    width: desktopWidth,
                    height: desktopHeight,
                    fit: 'fill',
                    focus: alignment,
                    format: 'jpg'
                })
            },
        ];
    };
    const promoImageSet = imageSet(promo?.mainImage?.image?.file?.url);
    const teaserImageSet = imageSet(promo?.teaserImage?.image?.file?.url);
    let contentIndex;
    promos.forEach((promoObj, index) => {
        if (promoObj.contentfulId === contentfulId) {
            contentIndex = index + 1;
        }
    });
    const handlePromoShown = useCallback(() => {
        const promoContent = isPromoContent(promo);
        const eventName = promoContent ? 'Promo Content Displayed' : 'Content Displayed';
        const trackingParams = {
            content_name: promoContent ? displayTitle : entryTitle,
            content_type: contentTypeId,
            content_id: contentfulId,
            content_tags: analyticsTags(tags),
            activation_date: activationDate,
            content_location: sectionTitle,
            content_index: contentIndex,
            location: pageName,
            cta_text: promoContent ? displayTitle : null
        };
        trackEvent(eventName, trackingParams);
    }, [
        activationDate,
        contentTypeId,
        contentfulId,
        displayTitle,
        entryTitle,
        promo,
        sectionTitle,
        tags,
    ]);
    useEffect(() => {
        if (promoInView) {
            handlePromoShown();
        }
    }, [handlePromoShown, promoInView]);
    const handlePromoClicked = () => {
        const promoContent = isPromoContent(promo);
        const eventName = promoContent ? 'Promo Content Clicked' : 'Content Clicked';
        const trackingParams = {
            content_name: promoContent ? displayTitle : entryTitle,
            content_type: contentTypeId,
            content_id: contentfulId,
            content_tags: analyticsTags(tags),
            activation_date: activationDate,
            content_location: sectionTitle,
            content_index: contentIndex,
            location: pageName,
            cta_text: promoContent ? displayTitle : null
        };
        trackEvent(eventName, trackingParams);
    };
    if (contentTypeId === 'userSpecificPromo') {
        /* istanbul ignore next */
        return (<UserSpecificPromo key={`promo-${externalId}`} className={s.promo} userSpecificPromo={promo}>
        {({ resultPromo }) => {
                if (resultPromo) {
                    return <ProfilePromo promo={resultPromo} section={section}/>;
                }
                return null;
            }}
      </UserSpecificPromo>);
    }
    if (contentTypeId === 'collection') {
        return (<div ref={promoRef} className={s.container}>
        <AnchorLink onClick={handlePromoClicked} href={`/collections/${externalId}`} title={displayTitle}>
          <div className={s.promo} key={`promo-${externalId}`}>
            {promo?.mainImage?.image.file?.url && (<ImageWrapper overlay zoomHover>
                <Image imageSet={promoImageSet} cover alt="profile promo"/>
              </ImageWrapper>)}
            <span className={s.title}>{displayTitle}</span>
          </div>
        </AnchorLink>
      </div>);
    }
    if (['article', 'writingByJKRowling'].includes(contentTypeId)) {
        const hubName = findHubName(tags);
        const articleImageSet = promo.teaserImage ? teaserImageSet : promoImageSet;
        return (<div ref={promoRef} className={s.container}>
        <AnchorLink onClick={handlePromoClicked} title={displayTitle} href={`/${hubName}/${externalId}`}>
          <div className={s.promo} key={`promo-${externalId}`}>
            {(promo?.teaserImage?.image?.file?.url || promo?.mainImage?.image?.file?.url) && (<ImageWrapper overlay zoomHover>
                <Image imageSet={articleImageSet} cover alt="profile promo"/>
              </ImageWrapper>)}
            <span className={s.title}>{displayTitle}</span>
          </div>
        </AnchorLink>
      </div>);
    }
    // Promo type + anything else that doesn't match.
    return (<div ref={promoRef} className={s.container}>
      <AnchorLink onClick={handlePromoClicked} title={displayTitle} href={promo.url}>
        <div className={s.promo} key={`promo-${externalId}`}>
          {promo?.mainImage?.image?.file?.url && (<ImageWrapper overlay zoomHover>
              <Image imageSet={promoImageSet} cover alt="profile promo"/>
            </ImageWrapper>)}
          <span className={s.title}>{promo.promoHeader || displayTitle}</span>
        </div>
      </AnchorLink>
    </div>);
};
ProfilePromo.propTypes = propTypes;
export default ProfilePromo;

    async function __Next_Translate__getStaticProps__195ca0f16be__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'src//Profile/components/ProfilePromo/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__195ca0f16be__ as getStaticProps }
  